import React from 'react';
import { Star, ExternalLink } from 'lucide-react';
import { motion } from 'framer-motion';
// Google "G" logo (multicolor)
const GoogleLogo = ({
  size = 20
}: {
  size?: number;
}) => <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 48 48" width={size} height={size} aria-hidden="true" data-id="element-238">
    <path fill="#FFC107" d="M43.611 20.083H42V20H24v8h11.303c-1.649 4.657-6.08 8-11.303 8-6.627 0-12-5.373-12-12s5.373-12 12-12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4 12.955 4 4 12.955 4 24s8.955 20 20 20 20-8.955 20-20c0-1.341-.138-2.65-.389-3.917z" data-id="element-239" />
    <path fill="#FF3D00" d="M6.306 14.691l6.571 4.819C14.655 15.108 18.961 12 24 12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4 16.318 4 9.656 8.337 6.306 14.691z" data-id="element-240" />
    <path fill="#4CAF50" d="M24 44c5.166 0 9.86-1.977 13.409-5.192l-6.19-5.238A11.91 11.91 0 0 1 24 36c-5.202 0-9.619-3.317-11.283-7.946l-6.522 5.025C9.505 39.556 16.227 44 24 44z" data-id="element-241" />
    <path fill="#1976D2" d="M43.611 20.083H42V20H24v8h11.303a12.04 12.04 0 0 1-4.087 5.571l.003-.002 6.19 5.238C36.971 39.205 44 34 44 24c0-1.341-.138-2.65-.389-3.917z" data-id="element-242" />
  </svg>;
const GOOGLE_REVIEWS_URL = 'https://maps.app.goo.gl/tccZHmTEbCQq71sK7?g_st=ig';
export function SocialProof() {
  const reviews = [{
    text: 'Finally found a landscaping company in San Diego that actually shows up and does the job right. My yard has never looked better. Highly recommend YEI Mateo!',
    author: 'Maria G.',
    location: 'El Cajon, CA',
    timeAgo: '2 weeks ago',
    avatarColor: 'bg-emerald-600'
  }, {
    text: 'They installed pavers in my backyard and it turned out incredible. Fair price, clean work, no surprises. These guys are the real deal.',
    author: 'James R.',
    location: 'Santee, CA',
    timeAgo: '1 month ago',
    avatarColor: 'bg-blue-600'
  }, {
    text: "Weekly maintenance, tree trimming, and a full cleanup — all done by the same crew. Reliable, professional, and great communication. Won't use anyone else.",
    author: 'Sandra M.',
    location: 'Chula Vista, CA',
    timeAgo: '3 weeks ago',
    avatarColor: 'bg-orange-600'
  }];
  return <section className="py-20 bg-white" data-id="element-243">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-244">
        {/* Header */}
        <div className="text-center mb-12" data-id="element-245">
          <span className="text-primary font-bold tracking-wider text-sm uppercase mb-3 block" data-id="element-246">
            WHAT CUSTOMERS ARE SAYING
          </span>
          <h2 className="text-3xl md:text-4xl font-heading font-bold text-darkCharcoal mb-6" data-id="element-247">
            Real Homeowners. Real Reviews.
          </h2>

          {/* Google Rating Summary Card */}
          <motion.a href={GOOGLE_REVIEWS_URL} target="_blank" rel="noopener noreferrer" initial={{
          opacity: 0,
          y: 10
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="inline-flex items-center gap-4 bg-white border border-gray-200 rounded-2xl shadow-sm hover:shadow-md transition-shadow px-6 py-4" data-id="element-248">
            <GoogleLogo size={36} data-id="element-249" />
            <div className="text-left" data-id="element-250">
              <div className="flex items-center gap-2" data-id="element-251">
                <span className="text-2xl font-bold text-darkCharcoal" data-id="element-252">
                  5.0
                </span>
                <div className="flex gap-0.5" data-id="element-253">
                  {[...Array(5)].map((_, i) => <Star key={i} size={18} className="fill-yellow-400 text-yellow-400" data-id="element-254" />)}
                </div>
              </div>
              <p className="text-sm text-gray-600" data-id="element-255">Based on Google Reviews</p>
            </div>
          </motion.a>
        </div>

        {/* Reviews Grid - styled like Google review cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-10" data-id="element-256">
          {reviews.map((review, index) => <motion.div key={index} initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          delay: index * 0.1
        }} className="bg-white p-6 rounded-2xl border border-gray-200 shadow-sm hover:shadow-md transition-shadow flex flex-col h-full" data-id="element-257">
              {/* Review header — avatar + name + Google logo */}
              <div className="flex items-start justify-between mb-4" data-id="element-258">
                <div className="flex items-center gap-3" data-id="element-259">
                  <div className={`${review.avatarColor} text-white font-bold w-11 h-11 rounded-full flex items-center justify-center text-lg flex-shrink-0`} data-id="element-260">
                    {review.author.charAt(0)}
                  </div>
                  <div data-id="element-261">
                    <p className="font-semibold text-darkCharcoal leading-tight" data-id="element-262">
                      {review.author}
                    </p>
                    <p className="text-xs text-gray-500" data-id="element-263">{review.location}</p>
                  </div>
                </div>
                <GoogleLogo size={20} data-id="element-264" />
              </div>

              {/* Stars + time */}
              <div className="flex items-center gap-2 mb-3" data-id="element-265">
                <div className="flex gap-0.5" data-id="element-266">
                  {[...Array(5)].map((_, i) => <Star key={i} size={16} className="fill-yellow-400 text-yellow-400" data-id="element-267" />)}
                </div>
                <span className="text-xs text-gray-500" data-id="element-268">{review.timeAgo}</span>
              </div>

              {/* Review text */}
              <p className="text-gray-700 leading-relaxed flex-grow" data-id="element-269">
                {review.text}
              </p>
            </motion.div>)}
        </div>

        {/* CTA: View all reviews on Google */}
        <div className="text-center" data-id="element-270">
          <a href={GOOGLE_REVIEWS_URL} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 text-primary font-bold hover:text-primary/80 transition-colors" data-id="element-271">
            <GoogleLogo size={18} data-id="element-272" />
            See all reviews on Google
            <ExternalLink size={16} data-id="element-273" />
          </a>
        </div>
      </div>
    </section>;
}